export interface SplitResult {
	main: string;
	quoted: string;
}

/**
 * Splits sanitized HTML into a main body and a quoted section.
 *
 * Detection order (first match wins for each top-level node):
 *   1. <blockquote> elements
 *   2. Elements with class "gmail_quote"
 *   3. Elements with id "appendonsend"
 *   4. <pre>/<div> children whose text content consists entirely of ">"-prefixed lines
 *
 * Everything from the first detected quoted node onward is treated as quoted.
 * Nodes before it remain as the main content.
 */
export function splitQuotedContent(html: string): SplitResult {
	const doc = new DOMParser().parseFromString(`<div id="__split_root__">${html}</div>`, 'text/html');
	const root = doc.getElementById('__split_root__')!;
	const children = Array.from(root.childNodes);

	const splitIndex = children.findIndex(node => isQuotedNode(node));

	if (splitIndex === -1) {
		return {main: html, quoted: ''};
	}

	const mainNodes = children.slice(0, splitIndex);
	const quotedNodes = children.slice(splitIndex);

	return {
		main: nodesToHtml(mainNodes),
		quoted: nodesToHtml(quotedNodes),
	};
}

function nodesToHtml(nodes: ChildNode[]): string {
	const tmp = document.createElement('div');
	nodes.forEach(n => tmp.appendChild(n.cloneNode(true)));
	return tmp.innerHTML;
}

function isQuotedNode(node: ChildNode): boolean {
	if (node.nodeType !== Node.ELEMENT_NODE) return false;
	const el = node as Element;

	if (el.tagName === 'BLOCKQUOTE') return true;
	if (el.classList.contains('gmail_quote')) return true;
	if (el.id === 'appendonsend') return true;
	if (isGreaterThanPrefixBlock(el)) return true;

	return false;
}

function isGreaterThanPrefixBlock(el: Element): boolean {
	if (el.tagName !== 'PRE' && el.tagName !== 'DIV') return false;
	const text = el.textContent ?? '';
	const lines = text.split('\n').filter(l => l.trim() !== '');
	return lines.length > 0 && lines.every(l => l.trimStart().startsWith('>'));
}
